import { AUTH_CONTENT } from '@/features/auth/constants/authContent'
import { env } from '@/config/env'
import type { MobilePlatform } from '../utils/detectMobilePlatform'
import { AppStoreBadgeLink, GooglePlayBadgeLink } from './StoreBadges'

type MobileAppChooserProps = {
  platform: Exclude<MobilePlatform, null>
  onContinueOnWeb: () => void
}

const COPY = {
  title: 'Continue with Liberty Rent',
  subtitle:
    'For the best experience, install the Liberty Rent app. You can also continue this invitation in your browser.',
  installHeading: 'Install the app',
  continueWeb: 'Continue on web',
} as const

export function MobileAppChooser({ platform, onContinueOnWeb }: MobileAppChooserProps) {
  const storeUrl = platform === 'android' ? env.playStoreUrl : env.appStoreUrl
  const logoSrc = AUTH_CONTENT.login.defaultLogoSrc

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-8"
      data-testid="apply-mobile-app-chooser"
    >
      <div className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex flex-col items-center bg-gray-50 px-6 py-8">
          <img
            src={logoSrc}
            alt={AUTH_CONTENT.login.logo.title}
            className="h-14 w-auto"
          />
        </div>

        <div className="px-6 py-8 sm:px-8">
          <h1 className="text-center text-2xl font-bold text-primary">
            {COPY.title}
          </h1>
          <p className="mt-3 text-center text-sm leading-relaxed text-gray-600">
            {COPY.subtitle}
          </p>

          <div className="mt-8 space-y-4">
            <p className="text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
              {COPY.installHeading}
            </p>

            <div className="flex justify-center">
              {platform === 'android' ? (
                <GooglePlayBadgeLink href={storeUrl} />
              ) : (
                <AppStoreBadgeLink href={storeUrl} />
              )}
            </div>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center" aria-hidden>
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs font-medium uppercase tracking-wide text-gray-400">
                  or
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={onContinueOnWeb}
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-800 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              data-testid="apply-continue-on-web"
            >
              {COPY.continueWeb}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
